import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { RefObject, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataCollector from "../../data-collector/DataCollector/DataCollector";
import DataWriter from "../../data-writer/DataWriter/DataWriter";
import { SelectButton } from "primereact/selectbutton";
import UserService from "../../../service/UserService";
import AppDialogs from "../../dialogs/AppDialogs";
import useDataHubStore from "store/dataHub-store";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Pages } from "util/enums";
import "./AppHeader.scss";
import { globalState } from "../../../context/GlobalStates";

type AppHeaderProps = {
  dataCollectorRef: RefObject<DataCollector>;
  dataWriterRef: RefObject<DataWriter>;
  jobRunnerRef: RefObject<HTMLDivElement>;
};

export const getPage = () => {
  const prefix = "#/";
  if (window.location.hash === `${prefix}${Pages.DATA_WRITER}`) {
    return Pages.DATA_WRITER;
  } else if (window.location.hash === `${prefix}${Pages.JOB_RUNNER}`) {
    return Pages.JOB_RUNNER;
  } else if (window.location.hash === `${prefix}${Pages.DATASETS_PAGE}`) {
    return Pages.DATASETS_PAGE;
  } else {
    return Pages.DATA_COLLECTOR;
  }
};

const AppHeader = ({
  dataCollectorRef,
  dataWriterRef,
  jobRunnerRef,
}: AppHeaderProps) => {
  const menuReader = useRef<Menu | null>(null);
  const menuWriter = useRef<Menu | null>(null);
  const menuJobRunner = useRef<Menu | null>(null);
  const [selectedPage, setSelectedPage] = useState<string>(getPage());
  const [dialogName, setDialogName] = useState<string | undefined>(undefined);
  const navigate = useNavigate();

  const isConfigEdited = useDataHubStore((state) => state.isConfigEdited);
  const setIsConfigEdited = useDataHubStore((state) => state.setIsConfigEdited);

  const dataPages = [
    {
      name: "Data Collector",
      value: Pages.DATA_COLLECTOR,
      route: Pages.DATA_COLLECTOR,
    },
    {
      name: "Data Writer",
      value: Pages.DATA_WRITER,
      route: Pages.DATA_WRITER,
    },
    {
      name: "Job Runner",
      value: Pages.JOB_RUNNER,
      route: Pages.JOB_RUNNER,
    },
  ];

  const settingsReader = [
    {
      label: "Reload Readers",
      icon: "pi pi-fw pi-sync",
      command: () => {
        dataCollectorRef.current?.restartCollector();
      },
    },
    {
      label: "Server Info",
      icon: "pi pi-fw pi-info-circle",
      command: () => {
        setDialogName("info");
      },
    },
    {
      label: "Run configuration backup",
      icon: "pi pi-fw pi-save",
      command: () => {
        dataCollectorRef.current?.runConfigurationBackup();
      },
    },
    {
      label: "Manual",
      icon: "pi pi-fw pi-map",
      command: () => {
        window.open(process.env.REACT_APP_MANUAL_URL, "_blank");
      },
    },
    {
      label: "Collector Logs",
      icon: "pi pi-fw pi-book",
      command: () => {
        setDialogName("allCollectorLogs");
      },
    },
    {
      label: "Post Processors management",
      icon: "pi pi-fw pi-cog",
      command: () => {
        setDialogName("postProcessorsManagement");
      },
    },
    {
      label: "Deduplication",
      icon: "pi pi-fw pi-filter",
      command: () => {
        setDialogName("deduplication");
      },
    },
    {
      label: "Import",
      icon: "pi pi-fw pi-download",
      command: () => {
        setDialogName("import");
      },
    },
    {
      label: "Export",
      icon: "pi pi-fw pi-upload",
      command: () => {
        setDialogName("export");
      },
    },
    {
      label: "Ping",
      icon: "pi pi-fw pi-refresh",
      command: () => {
        setDialogName("ping");
      },
    },
    {
      label: "Traceroute",
      icon: "pi pi-fw pi-refresh",
      command: () => {
        setDialogName("traceroute");
      },
    },
    {
      label: "Test tag remapping",
      icon: "pi pi-fw pi-refresh",
      command: () => {
        setDialogName("testTagRemapping");
      },
    },
    {
      label: "Base64",
      icon: "pi pi-fw pi-qrcode",
      command: () => {
        setDialogName("base64");
      },
    },
    {
      label: "Restart Reader",
      icon: "pi pi-fw pi-refresh",
      command: () => {
        dataCollectorRef.current?.shutdownReader();
      },
    },
  ];

  const settingsWriter = [
    {
      label: "Reload Pipelines",
      icon: "pi pi-fw pi-sync",
      command: () => {
        dataWriterRef.current?.restartWriter();
      },
    },
    {
      label: "Server Info",
      icon: "pi pi-fw pi-info-circle",
      command: () => {
        setDialogName("info");
      },
    },
    {
      label: "Run configuration backup",
      icon: "pi pi-fw pi-save",
      command: () => {
        dataWriterRef.current?.runConfigurationBackup();
      },
    },
    {
      label: "Manual",
      icon: "pi pi-fw pi-map",
      command: () => {
        window.open(process.env.REACT_APP_MANUAL_URL, "_blank");
      },
    },
    {
      label: "Writer logs",
      icon: "pi pi-fw pi-book",
      command: () => {
        setDialogName("allWriterLogs");
      },
    },
    {
      label: "Import",
      icon: "pi pi-fw pi-download",
      command: () => {
        setDialogName("import");
      },
    },
    {
      label: "Export",
      icon: "pi pi-fw pi-upload",
      command: () => {
        setDialogName("export");
      },
    },
    {
      label: "Ping",
      icon: "pi pi-fw pi-refresh",
      command: () => {
        setDialogName("ping");
      },
    },
    {
      label: "Traceroute",
      icon: "pi pi-fw pi-refresh",
      command: () => {
        setDialogName("traceroute");
      },
    },
    {
      label: "Base64",
      icon: "pi pi-fw pi-qrcode",
      command: () => {
        setDialogName("base64");
      },
    },
    {
      label: "Restart Writer",
      icon: "pi pi-fw pi-refresh",
      command: () => {
        dataWriterRef.current?.shutdownWriter();
      },
    },
  ];

  const settingsJobRunner = [
    {
      label: "Server Info",
      icon: "pi pi-fw pi-info-circle",
      command: () => {
        setDialogName("info");
      },
    },
    {
      label: "Manual",
      icon: "pi pi-fw pi-map",
      command: () => {
        window.open(process.env.REACT_APP_MANUAL_URL, "_blank");
      },
    },
    {
      label: "Python Environment Manager",
      icon: "pi pi-fw pi-server",
      command: () => {
        setDialogName("pythonEnvironments");
      },
    },
  ];

  const handlePageClick = (
    event: any,
    data: { name: string; route: string }
  ) => {
    const route = data.route || Pages.DATA_COLLECTOR;

    if (event.ctrlKey || event.metaKey || event.button === 1) {
      event.preventDefault();
      event.stopPropagation();
      const baseUrl = window.location.href.split("#")[0];
      window.open(`${baseUrl}#/${route}`, "_blank");
      return;
    }

    if (selectedPage === route) return;

    if (isConfigEdited) {
      event.preventDefault();
      confirmDialog({
        message:
          "This tab contains unsaved changes. Do you still wish to change the tab?",
        header: "Unsaved Changes",
        acceptClassName: "p-button-danger",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Change",
        rejectLabel: "Stay",
        accept: () => {
          setSelectedPage(route);
          globalState.setState({ selectedPage: route });
          navigate(route);
          setIsConfigEdited(false);
        },
      });
    } else {
      setSelectedPage(route);
      globalState.setState({ selectedPage: route });
      navigate(route);
      setIsConfigEdited(false);
    }
  };

  const goToPage = (data: { name: string; route: string }) => {
    return (
      <span
        style={{ textDecoration: "none", color: "white", cursor: "pointer" }}
        onMouseDown={(e) => {
          if (e.button === 1) {
            e.preventDefault();
            e.stopPropagation();
            const baseUrl = window.location.href.split("#")[0];
            window.open(`${baseUrl}#/${data.route}`, "_blank");
          }
        }}
        onClick={(e) => handlePageClick(e, data)}
      >
        {data.name}
      </span>
    );
  };

  return (
    <div className="card header-full-window">
      <ConfirmDialog />
      <div className="header-full-window-left-side">
        <img
          alt="logo"
          src={process.env.PUBLIC_URL + "/Dween.png"}
          height={"35px"}
          className="mr-1"
        />
        <SelectButton
          value={selectedPage}
          options={dataPages}
          onChange={(e) => {
            if (isConfigEdited) return;
            handlePageClick(e, { name: e.value, route: e.value });
          }}
          optionLabel="name"
          unselectable={false}
          itemTemplate={goToPage}
        />
      </div>
      <div className="flex justify-content-between header-full-window-right-side gap-5">
        <Button
          label="Datasets"
          className={`${
            selectedPage === "datasets" ? "selected-button" : "datasets-button"
          }`}
          style={{
            transform: "scale(95%)",
            fontWeight: "bold",
          }}
          onClick={(e) =>
            handlePageClick(e, { name: "Datasets", route: Pages.DATASETS_PAGE })
          }
        />
        {(selectedPage === Pages.DATA_COLLECTOR ||
          selectedPage === Pages.DATA_WRITER ||
          selectedPage === Pages.JOB_RUNNER) && (
          <Button
            label="Utilities"
            className="p-button-sm button-secondary-homemade"
            style={{ transform: "scale(95%)", fontWeight: "bolder" }}
            icon="pi pi-cog"
            onClick={(event) => {
              if (getPage() === Pages.DATA_COLLECTOR) {
                menuReader.current?.toggle(event);
              } else if (getPage() === Pages.DATA_WRITER) {
                menuWriter.current?.toggle(event);
              } else if (getPage() === Pages.JOB_RUNNER) {
                menuJobRunner.current?.toggle(event);
              }
            }}
            aria-controls="popup_menu"
            aria-haspopup
          />
        )}
        {UserService.isLoggedIn() && (
          <Button
            icon="pi pi-power-off"
            className="p-button-plain p-button-rounded p-button-danger ml-2"
            onClick={() => UserService.doLogout()}
          />
        )}
      </div>

      <Menu
        model={settingsReader}
        popup
        ref={menuReader}
        id="popup_menu_reader"
      />
      <Menu
        model={settingsWriter}
        popup
        ref={menuWriter}
        id="popup_menu_writer"
      />
      <Menu
        model={settingsJobRunner}
        popup
        ref={menuJobRunner}
        id="popup_menu_job_runner"
      />
      <AppDialogs
        dialogName={dialogName}
        close={() => {
          setDialogName(undefined);
        }}
      />
    </div>
  );
};

export default AppHeader;
